import type { UseQueryResult } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { ApiError, errorMessage } from './api';
import { cn } from './format';
import { Icon, type IconName } from './icons';
import { Alert, Button, EmptyState, Skeleton } from './primitives';

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
  /** Mobil kart görünümünde gösterilmez. */
  hideOnMobile?: boolean;
  /** Mobil kartta başlık satırı olarak (etiketsiz, kalın) gösterilir. */
  primary?: boolean;
}

/**
 * Masaüstünde tablo, mobilde (md altı) kart listesi. Satır tıklanabilir;
 * `actions` her satırın sağında (mobilde kartın altında) gösterilir.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  actions,
  empty,
  loading,
  className,
  rowClassName,
}: {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  actions?: (row: T) => ReactNode;
  empty?: ReactNode;
  loading?: boolean;
  className?: string;
  rowClassName?: (row: T) => string | undefined;
}) {
  if (loading && !rows) return <TableSkeleton />;
  if (!rows?.length) return <>{empty ?? <EmptyState title="Kayıt yok" />}</>;
  const align = (a?: 'left' | 'right' | 'center') => (a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left');
  const primaryCol = columns.find((c) => c.primary) ?? columns[0];
  const rest = columns.filter((c) => c !== primaryCol && !c.hideOnMobile);
  return (
    <div className={className}>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-xs text-muted">
              {columns.map((c) => (
                <th key={c.key} scope="col" className={cn('px-3 py-2 font-medium whitespace-nowrap', align(c.align), c.className)}>
                  {c.header}
                </th>
              ))}
              {actions ? <th className="px-3 py-2" aria-label="İşlemler" /> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={rowKey(r)}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                className={cn('border-b border-border/70 last:border-0', onRowClick && 'cursor-pointer hover:bg-surface/60', rowClassName?.(r))}
              >
                {columns.map((c) => (
                  <td key={c.key} className={cn('px-3 py-2.5 align-middle', align(c.align), c.className)}>
                    {c.cell(r)}
                  </td>
                ))}
                {actions ? (
                  <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-1">{actions(r)}</div>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="flex flex-col gap-2 md:hidden">
        {rows.map((r) => (
          <li
            key={rowKey(r)}
            onClick={onRowClick ? () => onRowClick(r) : undefined}
            className={cn('rounded-[var(--radius)] border border-border bg-card p-3', onRowClick && 'cursor-pointer active:bg-surface', rowClassName?.(r))}
          >
            {primaryCol ? <div className="mb-2 flex items-center justify-between gap-2 font-medium">{primaryCol.cell(r)}</div> : null}
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
              {rest.map((c) => (
                <div key={c.key} className="min-w-0">
                  <dt className="text-xs text-muted">{c.header}</dt>
                  <dd className="truncate">{c.cell(r)}</dd>
                </div>
              ))}
            </dl>
            {actions ? (
              <div className="mt-3 flex flex-wrap gap-2 border-t border-border pt-3" onClick={(e) => e.stopPropagation()}>
                {actions(r)}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-3 py-2" aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const forbidden = error instanceof ApiError && error.status === 403;
  return (
    <Alert
      tone={forbidden ? 'warning' : 'danger'}
      title={forbidden ? 'Bu içeriğe erişim yetkiniz yok' : 'Yüklenemedi'}
      action={onRetry && !forbidden ? <Button size="sm" icon="refresh" onClick={onRetry}>Tekrar dene</Button> : undefined}
    >
      {errorMessage(error)}
    </Alert>
  );
}

/** Sorgu durumuna göre iskelet / hata / içerik. */
export function QueryView<T>({ query, children, skeleton, empty }: { query: UseQueryResult<T>; children: (data: T) => ReactNode; skeleton?: ReactNode; empty?: (data: T) => boolean | ReactNode }) {
  if (query.isPending) return <>{skeleton ?? <TableSkeleton />}</>;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (empty) {
    const e = empty(query.data);
    if (e === true) return <EmptyState title="Kayıt yok" />;
    if (e) return <>{e}</>;
  }
  return <>{children(query.data)}</>;
}

/** Sayfa numaralı gezinme (offset/limit). */
export function Pagination({ page, pageSize, total, onChange }: { page: number; pageSize: number; total?: number; onChange: (page: number) => void }) {
  const pages = total !== undefined ? Math.max(1, Math.ceil(total / pageSize)) : undefined;
  return (
    <div className="mt-4 flex items-center justify-between gap-2 text-sm">
      <span className="text-muted">{pages ? `Sayfa ${page} / ${pages}` : `Sayfa ${page}`}</span>
      <div className="flex gap-2">
        <Button size="sm" icon="chevronLeft" disabled={page <= 1} onClick={() => onChange(page - 1)}>
          Önceki
        </Button>
        <Button size="sm" disabled={pages !== undefined && page >= pages} onClick={() => onChange(page + 1)}>
          Sonraki
          <Icon name="chevronRight" size={16} />
        </Button>
      </div>
    </div>
  );
}

/** İmleç tabanlı sayfalama için "Daha fazla" butonu. */
export function LoadMore({ hasMore, loading, onClick }: { hasMore: boolean; loading?: boolean; onClick: () => void }) {
  if (!hasMore) return null;
  return (
    <div className="mt-4 flex justify-center">
      <Button loading={loading} onClick={onClick}>
        Daha fazla yükle
      </Button>
    </div>
  );
}

export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Yatay kaydırılabilir filtre çubuğu (mobilde taşmaz). */
export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center', className)}>{children}</div>;
}

export function IconText({ icon, children, className }: { icon: IconName; children: ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <Icon name={icon} size={14} />
      {children}
    </span>
  );
}
